import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Copy,
  Database,
  Flag,
  ImageIcon,
  Keyboard,
  MessageSquarePlus,
  Moon,
  RefreshCw,
  Search,
  ThumbsDown,
  ThumbsUp,
  Zap,
} from "lucide-react";
import { useTheme, type ResolvedTheme } from "@/hooks/useTheme";

// ═════════════════════════════════════════════════════════════════════════════
// Brand marks — real logos for the sources and Teams references.
// ═════════════════════════════════════════════════════════════════════════════

function GitHubMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.5 11.5 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.014 2.898-.014 3.293 0 .322.216.694.825.576C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function LinearMark({ className = "", style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill="currentColor" aria-hidden>
      <path d="M1.225 61.523c-.222-.949.908-1.546 1.597-.857l36.512 36.512c.69.69.092 1.819-.857 1.597C20.485 94.84 5.16 79.515 1.225 61.523Z" />
      <path d="M.002 46.889a.99.99 0 0 0 .29.76l52.06 52.058a.99.99 0 0 0 .76.29c2.124-.106 4.21-.353 6.25-.73.722-.134.96-1.012.442-1.53L2.263 40.197c-.518-.518-1.396-.28-1.53.442-.378 2.04-.625 4.126-.731 6.25Z" />
      <path d="M4.072 29.043a.99.99 0 0 0 .213 1.087l65.585 65.585a.99.99 0 0 0 1.087.213c1.498-.609 2.946-1.31 4.34-2.094a.984.984 0 0 0 .204-1.557L7.722 24.5a.984.984 0 0 0-1.557.205 49.41 49.41 0 0 0-2.093 4.339Z" />
      <path d="M12.291 16.32a.987.987 0 0 1-.045-1.353C21.357 5.521 34.046-.182 48.069-.182c26.539 0 48.052 21.513 48.052 48.052 0 14.023-5.703 26.712-15.149 35.823a.987.987 0 0 1-1.354-.045L12.292 16.32Z" />
    </svg>
  );
}

function TeamsMark({ className = "" }: { className?: string }) {
  return <img src="/teams-logo.png" alt="" aria-hidden className={className} draggable={false} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// Figure — inline screenshot. Serves the theme-matched file from
// /public/help-media and falls back to a labeled placeholder until the real
// image is added. This folder is deliberately not named "help" — a real
// directory named /help on the server makes Express redirect GET /help to
// /help/, which breaks the exact-path route match in main.tsx.
// ═════════════════════════════════════════════════════════════════════════════

interface FigureProps {
  /** Base name, e.g. "welcome" → /help/welcome-light.png or /help/welcome-dark.png */
  name: string;
  alt: string;
  caption?: string;
  theme: ResolvedTheme;
  /** When false, loads /help/<name>.png with no theme suffix. */
  themed?: boolean;
  /** For tall/narrow crops (e.g. an icon rail) — caps width instead of stretching full-bleed. */
  maxWidthPx?: number;
}

function Figure({ name, alt, caption, theme, themed = true, maxWidthPx }: FigureProps) {
  const src = themed ? `/help-media/${name}-${theme}.png` : `/help-media/${name}.png`;
  const [errored, setErrored] = useState(false);

  useEffect(() => setErrored(false), [src]);

  return (
    <figure className="my-6">
      <div
        className={`overflow-hidden rounded-xl border ${maxWidthPx ? "mx-auto" : ""}`}
        style={{
          borderColor: "var(--border)",
          background: "var(--surface-elevated)",
          ...(maxWidthPx ? { maxWidth: maxWidthPx } : {}),
        }}
      >
        {errored ? (
          <div
            className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center"
            style={{ color: "var(--text-muted)" }}
          >
            <ImageIcon className="h-7 w-7 opacity-50" />
            <p className="text-[12.5px] font-medium">Screenshot coming soon</p>
            <p className="text-[11px] opacity-70">
              Add <code className="font-mono">{themed ? `${name}-${theme}.png` : `${name}.png`}</code> to{" "}
              <code className="font-mono">frontend/public/help-media/</code>
            </p>
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            onError={() => setErrored(true)}
            className={maxWidthPx ? "block w-full h-auto" : "block w-full"}
          />
        )}
      </div>
      {caption && (
        <figcaption className="mt-2 text-center text-[12px]" style={{ color: "var(--text-muted)" }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Small building blocks
// ═════════════════════════════════════════════════════════════════════════════

function SectionHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="scroll-mt-20 text-[22px] font-semibold text-text">
      {children}
    </h2>
  );
}

function Lead({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
      {children}
    </p>
  );
}

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="mt-4 space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
            style={{ background: "var(--accent)", color: "var(--accent-fg, #fff)" }}
          >
            {i + 1}
          </span>
          <span className="text-[14px] leading-relaxed text-text">{item}</span>
        </li>
      ))}
    </ol>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Table of contents
// ═════════════════════════════════════════════════════════════════════════════

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "asking", label: "Asking a question" },
  { id: "history", label: "Your chat history" },
  { id: "answers", label: "Working with an answer" },
  { id: "feedback", label: "Sharing feedback" },
  { id: "teams", label: "Using it in Microsoft Teams" },
  { id: "preferences", label: "Appearance & controls" },
  { id: "how", label: "How it finds answers" },
  { id: "next", label: "What's coming next" },
  { id: "help", label: "Need more help?" },
];

// Response action rows
const ACTIONS: { icon: React.ElementType; name: string; what: string }[] = [
  { icon: Copy, name: "Copy", what: "Copies the full answer to your clipboard to paste elsewhere." },
  { icon: RefreshCw, name: "Regenerate", what: "Asks the assistant to answer again. Shown on the latest reply only." },
  { icon: ThumbsUp, name: "Good answer", what: "Tells the team the answer was helpful. Helps improve quality." },
  { icon: ThumbsDown, name: "Bad answer", what: "Flags the answer as unhelpful so the team can review it." },
  { icon: Flag, name: "Report a problem", what: "Opens a short form to describe exactly what was wrong." },
];

// Coming-soon rows
const ROADMAP = [
  { icon: Zap, title: "Even faster answers", description: "Common lookups remembered briefly, so repeat questions return in seconds." },
  { icon: Database, title: "More knowledge sources", description: "Beyond GitHub and Linear — SharePoint, internal guides, and more over time." },
];

// ═════════════════════════════════════════════════════════════════════════════
// Page
// ═════════════════════════════════════════════════════════════════════════════

interface HelpPageProps {
  /** Called instead of navigating to "/" when Help is rendered inside the
   * running app (e.g. from the sidebar). Falls back to a real navigation
   * when the page was opened directly at the /help URL, since there is no
   * app state to return to in that case. */
  onBack?: () => void;
}

export function HelpPage({ onBack }: HelpPageProps = {}) {
  const { resolved } = useTheme();
  const [activeSection, setActiveSection] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Set<string>();

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) visibleSections.add(id);
          else visibleSections.delete(id);
          // Pick the first SECTIONS entry that is currently visible
          const first = SECTIONS.find((s) => visibleSections.has(s.id));
          if (first) setActiveSection(first.id);
        },
        { rootMargin: "-20% 0px -60% 0px" },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-30 flex h-[52px] items-center border-b border-border bg-surface px-6">
        <div className="flex items-center gap-2.5">
          <img src="/ndc-logo.svg" alt="National Datacare" className="h-7 w-auto select-none dark:hidden" draggable={false} />
          <img src="/ndc-logo-white.svg" alt="" className="hidden h-7 w-auto select-none dark:block" draggable={false} />
          <span aria-hidden className="h-5 w-px bg-border" />
          <span className="text-[12px] font-medium text-text-muted">Help &amp; Features</span>
        </div>
        <div className="ml-auto">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1.5 text-[12px] transition-colors hover:text-text"
              style={{ color: "var(--text-muted)" }}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to chat
            </button>
          ) : (
            <a
              href="/"
              className="flex items-center gap-1.5 text-[12px] transition-colors hover:text-text"
              style={{ color: "var(--text-muted)" }}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to chat
            </a>
          )}
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-12 lg:grid-cols-[220px_1fr]">

        {/* ── Table of contents (sticky on desktop) ───────────────────── */}
        <aside className="hidden lg:block">
          <nav className="sticky top-[76px]" aria-label="On this page">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              On this page
            </p>
            <ul className="space-y-1.5 border-l" style={{ borderColor: "var(--border)" }}>
              {SECTIONS.map((s) => {
                const isActive = s.id === activeSection;
                return (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      aria-current={isActive ? "location" : undefined}
                      className="-ml-px block border-l-2 pl-3 text-[13px] transition-colors hover:text-text"
                      style={{
                        borderColor: isActive ? "var(--ndc-red)" : "transparent",
                        color: isActive ? "var(--ndc-red)" : "var(--text-muted)",
                        fontWeight: isActive ? 600 : undefined,
                      }}
                    >
                      {s.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* ── Article ──────────────────────────────────────────────────── */}
        <article className="min-w-0 max-w-3xl">

          {/* Title block */}
          <div className="mb-8">
            <p className="text-[12.5px] font-medium" style={{ color: "var(--text-muted)" }}>
              NDC Knowledge Assistant · User guide
            </p>
            <h1 className="mt-2 font-serif text-[32px] font-semibold leading-tight text-text">
              Getting started with the NDC Knowledge Assistant
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
              The NDC Knowledge Assistant answers questions about NDC's software and projects in
              everyday language. It looks up the real answer from NDC's code and project tickets — and
              shows you where each answer came from, so you never have to take it on faith.
            </p>
          </div>

          {/* ── Overview ─────────────────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="overview">Overview</SectionHeading>
            <Lead>
              You can open the assistant in your web browser or as a tab inside Microsoft Teams. Both
              work the same way. Here's the full screen, with the chat in the middle and your saved
              conversations on the left.
            </Lead>
            <Figure
              name="app-full"
              theme={resolved}
              alt="The NDC Knowledge Assistant with the sidebar of saved chats on the left and a conversation in the center."
              caption="The main screen: saved conversations on the left, the chat in the center."
            />
          </section>

          {/* ── Asking a question ────────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="asking">Asking a question</SectionHeading>
            <Lead>
              Type your question in plain English in the box at the bottom and press send. When you
              open a new chat, a few suggested questions appear — click any of them to ask it
              instantly.
            </Lead>
            <Figure
              name="welcome"
              theme={resolved}
              alt="The welcome screen with suggested starter questions."
              caption="Suggested questions on the welcome screen."
            />
            <p className="mt-4 text-[14px] font-medium text-text">Good questions to try</p>
            <ul className="mt-2 space-y-2">
              {[
                "What is rfms-web?",
                "Does the family portal support online payments?",
                "Who owns rfms-vendor-api?",
                "What happens when a resident is discharged?",
                "What are the open tickets for the Knowledge Assistant?",
              ].map((q) => (
                <li key={q} className="flex items-start gap-2.5 text-[14px] text-text">
                  <Search className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
                  {q}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Tip: name the portal or system you mean for the sharpest answer. If the assistant can't
              confirm something from the sources, it will tell you rather than guess.
            </p>
          </section>

          {/* ── Chat history ─────────────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="history">Your chat history</SectionHeading>
            <Lead>
              Every conversation is saved automatically and listed in the left sidebar. Open the
              sidebar with the panel button in the top-left corner.
            </Lead>
            <div className="mt-2 grid grid-cols-1 gap-6 sm:grid-cols-[220px_1fr] sm:items-start">
              <Figure
                name="sidebar"
                theme={resolved}
                alt="The sidebar listing several saved conversations, most recent at the top."
                caption="Your recent conversations, newest first."
                maxWidthPx={220}
              />
              <div>
                <ul className="space-y-2 text-[14px] text-text">
                  <li className="flex items-start gap-2.5">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--accent)", opacity: 0.7 }} />
                    Click any conversation to reopen it exactly where you left off.
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--accent)", opacity: 0.7 }} />
                    Hover over a chat and click the trash icon to delete it.
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--accent)", opacity: 0.7 }} />
                    Start a fresh conversation any time with “New Chat” at the top of the list.
                  </li>
                </ul>
                <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  Your conversations are saved to your account, so they follow you no matter which
                  computer or browser you sign in from.
                </p>
              </div>
            </div>
          </section>

          {/* ── Working with an answer ───────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="answers">Working with an answer</SectionHeading>
            <Lead>
              Every answer lists the sources it used, shown as small chips you can recognize. Below
              each answer is a row of quick actions.
            </Lead>
            <Figure
              name="answer-actions"
              theme={resolved}
              alt="An answer showing its source chips and the row of action buttons below it."
              caption="Source chips and the action row beneath an answer."
            />
            <div
              className="mt-5 overflow-hidden rounded-xl border"
              style={{ borderColor: "var(--border)" }}
            >
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr style={{ background: "var(--surface-muted)" }}>
                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                      Button
                    </th>
                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                      What it does
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ACTIONS.map(({ icon: Icon, name, what }) => (
                    <tr key={name} className="border-t" style={{ borderColor: "var(--border)" }}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="flex items-center gap-2 text-[13.5px] font-medium text-text">
                          <Icon className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                          {name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[13.5px]" style={{ color: "var(--text-muted)" }}>{what}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Sharing feedback ─────────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="feedback">Sharing feedback</SectionHeading>
            <Lead>
              Use the <span className="font-medium text-text">Feedback</span> button in the top-right
              corner to suggest a feature, report a bug, or share anything on your mind. The assistant
              writes your note up as a clear ticket for the team before it's sent.
            </Lead>
            <Steps
              items={[
                <>Click <span className="font-medium text-text">Feedback</span> in the top-right corner.</>,
                <>Choose a category (optional) so it reaches the right people.</>,
                <>Describe what you'd like — then select <span className="font-medium text-text">Preview &amp; submit</span>.</>,
                <>If someone already raised the same idea, your vote is added to theirs instead of creating a duplicate.</>,
              ]}
            />
            <Figure
              name="feedback"
              theme={resolved}
              alt="The feedback panel with a category selected and a typed suggestion."
              caption="The feedback panel."
            />
          </section>

          {/* ── Teams ────────────────────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="teams">Using it in Microsoft Teams</SectionHeading>
            <Lead>
              The assistant is available as a personal tab in Microsoft Teams, so it's one click away
              while you work. Pin it once and it stays in your Teams sidebar.
            </Lead>
            <Steps
              items={[
                <>In Teams, open <span className="font-medium text-text">Apps</span> from the left rail.</>,
                <>Find <span className="font-medium text-text">NDC Assistant</span> and add it. (Your IT admin may need to install it for you.)</>,
                <>Right-click the app icon in the sidebar and choose <span className="font-medium text-text">Pin</span>.</>,
                <>Open the tab and ask a question — it works exactly like the browser version.</>,
              ]}
            />
            <Figure
              name="teams-tab"
              theme={resolved}
              alt="The NDC Knowledge Assistant running inside a Microsoft Teams tab."
              caption="The assistant running as a tab inside Microsoft Teams."
            />
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-[140px_1fr] sm:items-center">
              <Figure
                name="teams-pinned"
                themed={false}
                theme={resolved}
                alt="The Teams left sidebar showing the pinned NDC Assistant app."
                caption="Pinned for quick access."
                maxWidthPx={140}
              />
              <div>
                <p className="text-[14px] font-semibold text-text">Pinned once, always within reach</p>
                <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  Once pinned, the NDC Assistant icon stays in your Teams left rail — no need to dig
                  through Apps to find it again.
                </p>
              </div>
            </div>
            <div
              className="mt-4 flex items-center gap-3 rounded-xl border p-4"
              style={{ borderColor: "var(--border)", background: "var(--surface)" }}
            >
              <TeamsMark className="h-8 w-8 shrink-0" />
              <p className="text-[13px]" style={{ color: "var(--text-muted)" }}>
                The tab also matches your Teams light or dark theme automatically.
              </p>
            </div>
          </section>

          {/* ── Appearance & controls ────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="preferences">Appearance &amp; controls</SectionHeading>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { icon: Moon, title: "Light & dark mode", text: "The app matches your device or Teams theme automatically — there's nothing to set up." },
                { icon: Keyboard, title: "Stop a reply", text: "Press Escape to stop an answer that's still being written. What's there stays on screen." },
              ].map(({ icon: Icon, title, text }) => (
                <div
                  key={title}
                  className="flex gap-3 rounded-xl border p-4"
                  style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                    style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-text">{title}</p>
                    <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--text-muted)" }}>{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── How it finds answers ─────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="how">How it finds answers</SectionHeading>
            <Lead>
              You don't need to know the details, but here's the short version of what happens after
              you press send.
            </Lead>
            <Steps
              items={[
                <>Your question is turned into a few short search terms.</>,
                <>Those terms are used to search NDC's real sources, and the most relevant material is read in full.</>,
                <>A plain-English answer is written from what was found, with the sources listed underneath.</>,
              ]}
            />
            <div
              className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border p-5"
              style={{ borderColor: "var(--border)", background: "var(--surface)" }}
            >
              <span className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                Sources today
              </span>
              <span className="flex items-center gap-2 text-[14px] font-medium text-text">
                <GitHubMark className="h-5 w-5" /> GitHub
                <span className="text-[12px] font-normal" style={{ color: "var(--text-muted)" }}>· code &amp; docs</span>
              </span>
              <span className="flex items-center gap-2 text-[14px] font-medium text-text">
                <LinearMark className="h-5 w-5" style={{ color: "#5E6AD2" }} /> Linear
                <span className="text-[12px] font-normal" style={{ color: "var(--text-muted)" }}>· project tickets</span>
              </span>
            </div>
            <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Because every answer comes from real sources, you can trust it for real work — and when
              the answer isn't there, the assistant says so honestly.
            </p>
          </section>

          {/* ── What's coming next ───────────────────────────────────── */}
          <section className="mb-12">
            <SectionHeading id="next">What's coming next</SectionHeading>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {ROADMAP.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="rounded-xl border border-dashed p-5"
                  style={{ background: "var(--surface-elevated)", borderColor: "var(--border)" }}
                >
                  <div
                    className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg"
                    style={{ background: "var(--surface-muted)", color: "var(--text-muted)" }}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </div>
                  <p className="text-[14px] font-semibold text-text">{title}</p>
                  <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--text-muted)" }}>{description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Need more help ───────────────────────────────────────── */}
          <section>
            <SectionHeading id="help">Need more help?</SectionHeading>
            <Lead>
              Can't find what you're looking for, or something isn't working? Use the
              <span className="font-medium text-text"> Feedback</span> button in the chat and the team
              will help.
            </Lead>
            <div className="mt-4">
              <a
                href="/"
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[13.5px] font-medium"
                style={{ background: "var(--button)", color: "var(--button-fg)" }}
              >
                <MessageSquarePlus className="h-4 w-4" />
                Back to the assistant
              </a>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}
